import { authRequest, request } from "~/utils/api";

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.projects)) return data.projects;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const normalizeItem = (data) => data?.project || data?.data || data;

/**
 * 我的项目列表（需登录）
 * @param {string} token
 * @returns {Promise<Array>}
 */
export const getProjects = (token) =>
  authRequest(token, { url: "/profile/projects" }).then((res) =>
    normalizeList(res.data)
  );

/**
 * @param {string} token
 * @param {Object} params
 * @returns {Promise<{projects: Array, pager: Object}>}
 */
export const getAdminProjects = (token, params) =>
  authRequest(token, {
    url: "/admin/projects",
    params,
  }).then((res) => res.data);

/**
 * 管理端下架项目（需管理员，强制下架，不拦截推广期）：从项目广场移除
 * @param {string} token
 * @param {string} id 项目 hashid
 * @returns {Promise<{message: string}>}
 */
export const unpublishAdminProject = (token, id) =>
  authRequest(token, {
    url: `/admin/projects/${id}/unpublish`,
    method: "PATCH",
  }).then((res) => res.data);

/**
 * 管理端恢复项目展示（需管理员）：重新展示在项目广场
 * @param {string} token
 * @param {string} id 项目 hashid
 * @returns {Promise<{message: string}>}
 */
export const restoreAdminProject = (token, id) =>
  authRequest(token, {
    url: `/admin/projects/${id}/restore`,
    method: "PATCH",
  }).then((res) => res.data);

/**
 * 管理端置顶推广审核列表（需管理员）
 * 后端固定每页 20 条，只支持 page / status；不传 status 返回全部状态
 * @param {string} token
 * @param {Object} params { status?: string, page?: number }
 * @returns {Promise<{promotions: Array, pager: Object}>}
 */
export const getAdminProjectPromotions = (token, params) =>
  authRequest(token, {
    url: "/admin/project_promotions",
    params,
  }).then((res) => res.data);

/**
 * 通过置顶推广申请（需管理员，仅待审核可操作）
 * 有空位时后端会立即开始推广，返回的 promotion.status 可能已是 active
 * @param {string} token
 * @param {string} id 推广订单 hashid
 * @returns {Promise<{promotion: Object}>}
 */
export const approveProjectPromotion = (token, id) =>
  authRequest(token, {
    url: `/admin/project_promotions/${id}/approve`,
    method: "PATCH",
  }).then((res) => res.data);

/**
 * 驳回置顶推广申请（需管理员，仅待审核可操作）：驳回后原路退回电量
 * @param {string} token
 * @param {string} id 推广订单 hashid
 * @param {string} [reason] 驳回理由，选填
 * @returns {Promise<{promotion: Object}>}
 */
export const rejectProjectPromotion = (token, id, reason = "") =>
  authRequest(token, {
    url: `/admin/project_promotions/${id}/reject`,
    method: "PATCH",
    data: { reason },
  }).then((res) => res.data);

/**
 * @param {string} token
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export const createProject = (token, data) =>
  authRequest(token, {
    url: "/projects",
    method: "POST",
    data,
  }).then((res) => normalizeItem(res.data));

/**
 * @param {string} token
 * @param {string} id
 * @returns {Promise<Object>}
 */
export const getProject = (token, id) =>
  authRequest(token, { url: `/projects/${id}` }).then((res) =>
    normalizeItem(res.data)
  );

/**
 * @param {string} token
 * @param {string} id
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export const updateProject = (token, id, data) =>
  authRequest(token, {
    url: `/projects/${id}`,
    method: "PATCH",
    data,
  }).then((res) => normalizeItem(res.data));

/**
 * @param {string} token
 * @param {string} id
 * @param {File} file
 * @returns {Promise<Object>}
 */
export const updateProjectLogo = (token, id, file) => {
  const formData = new window.FormData();
  formData.append("logo", file);

  return authRequest(token, {
    url: `/projects/${id}/logo`,
    method: "POST",
    headers: {
      "Content-Type": "multipart/form-data",
    },
    data: formData,
  }).then((res) => normalizeItem(res.data));
};

/**
 * @param {string} token
 * @param {string} id
 * @returns {Promise<Object>}
 */
export const deleteProject = (token, id) =>
  authRequest(token, {
    url: `/projects/${id}`,
    method: "DELETE",
  }).then((res) => res.data);

/**
 * 发布项目到广场（仅拥有者，无请求体）
 * 响应在 project 字段外还带 ele_reward（本次发布实际奖励的电量，无奖励为 0），
 * normalizeItem 只取 project 对象，这里手动拼回返回值
 * @param {string} token
 * @param {string} id
 * @returns {Promise<Object>} 项目字段 + ele_reward
 */
export const publishProject = (token, id) =>
  authRequest(token, {
    url: `/projects/${id}/publish`,
    method: "POST",
  }).then((res) => ({
    ...normalizeItem(res.data),
    ele_reward: res.data?.ele_reward || 0,
  }));

/**
 * 取消发布（仅拥有者，无请求体）
 * @param {string} token
 * @param {string} id
 * @returns {Promise<Object>}
 */
export const unpublishProject = (token, id) =>
  authRequest(token, {
    url: `/projects/${id}/publish`,
    method: "DELETE",
  }).then((res) => normalizeItem(res.data));

/**
 * 项目排行榜（公开）：只返回已发布项目，固定每页 20 条
 * @param {Object} params { sort: 'latest'|'hot', project_type, open_source, field_tag_id, page }
 * @returns {Promise<{projects: Array, pager: Object, impression_token: string}>}
 */
export const getProjectRankings = (params) =>
  request({ url: "/projects/rankings", params }).then((res) => res.data);

/**
 * 项目广场统计（公开）：项目总数 / 已发布数 / 曝光总和
 * @returns {Promise<Object>}
 */
export const getProjectStats = () =>
  request({ url: "/projects/stats" }).then((res) => res.data);

/**
 * 顶项目（需登录，幂等；每日限 3 个）
 * 实际新增顶时响应带 upvotes_remaining_today（顶完后今天剩余可顶次数），
 * 幂等重复顶不返回该字段；normalizeItem 只取项目对象，这里手动拼回返回值
 * @param {string} token
 * @param {string} id
 * @returns {Promise<Object>} 项目字段 + upvotes_remaining_today
 */
export const upvoteProject = (token, id) =>
  authRequest(token, {
    url: `/projects/${id}/upvote`,
    method: "POST",
  }).then((res) => ({
    ...normalizeItem(res.data),
    upvotes_remaining_today: res.data?.upvotes_remaining_today,
  }));

/**
 * 顶过项目的人（公开）：最近顶的在前，未登录也能读
 * 每页条数由前端决定（后端上限 100），弹窗收起态只要 8 个，展开态一次要几十个
 * @param {string} id 项目 hashid
 * @param {Object} [params] { page, perPage }
 * @returns {Promise<{users: Array, pager: Object}>}
 */
export const getProjectUpvoters = (id, { page = 1, perPage } = {}) =>
  request({
    url: `/projects/${id}/upvoters`,
    params: { page, per_page: perPage },
  }).then((res) => res.data);

/**
 * 记录项目被查看（公开，匿名可上报；指纹头由拦截器自动携带）
 * 登录用户带上 token 按用户身份统计去重，未登录传空即可
 * 同访客对同一项目每天最多计 3 次，返回的 views_count 为当前计数，不保证每次递增
 * @param {string} [token] 登录用户 token，未登录为 null/undefined
 * @param {string} id 项目 hashid
 * @returns {Promise<{views_count: number}>}
 */
export const recordProjectView = (token, id) =>
  authRequest(token, { url: `/projects/${id}/view`, method: "POST" }).then(
    (res) => res.data
  );

/**
 * 上报项目曝光（公开，匿名可上报；指纹头由拦截器自动携带）
 * 一次最多 5 个令牌，展示位置由令牌决定，前端不需要知道
 * 同一访客对同一项目的同一展示位置 30 分钟内只计 1 次，
 * 所以重复上报返回的 recorded 为 0 属正常，不要据此判断成功失败
 * @param {string[]} tokens 列表接口返回的 impression_token
 * @param {string} [token] 登录用户 token，未登录传 null/undefined
 * @returns {Promise<{recorded: number}>}
 */
export const recordProjectImpressions = (tokens, token) => {
  const valid = (tokens || []).filter(Boolean).slice(0, 5);
  if (!valid.length) return Promise.resolve({ recorded: 0 });

  return authRequest(token, {
    url: "/projects/impressions",
    method: "POST",
    data: { tokens: valid },
  }).then((res) => res.data);
};

/**
 * 广场「置顶推广中」列表（公开）：推广中的项目按下单顺序返回，可能为空
 * @returns {Promise<{promotions: Array, impression_token: string}>}
 */
export const getPromotedProjects = () =>
  request({ url: "/projects/promoted" }).then((res) => res.data);

/**
 * 项目的置顶推广状态（需登录 + 仅拥有者）
 * state: available 有空位 / full 满位排队 / queued 已在排队 / active 推广中
 * @param {string} token
 * @param {string} id 项目 hashid
 * @returns {Promise<Object>}
 */
export const getProjectPromotion = (token, id) =>
  authRequest(token, { url: `/projects/${id}/promotion` }).then(
    (res) => res.data
  );

/**
 * 购买置顶推广（需登录 + 仅拥有者）：下单即扣电量并占排队序位
 * @param {string} token
 * @param {string} id 项目 hashid
 * @param {number} durationDays 档位：1 / 7 / 30 天
 * @returns {Promise<Object>} 与 getProjectPromotion 相同的状态结构
 */
export const createProjectPromotion = (token, id, durationDays) =>
  authRequest(token, {
    url: `/projects/${id}/promotion`,
    method: "POST",
    data: { duration_days: durationDays },
  }).then((res) => res.data);

/**
 * 取消排队中的置顶推广（需登录 + 仅拥有者）：电量原路退回
 * 推广中（active）不可取消，后端返回 project_promotion_not_cancelable
 * @param {string} token
 * @param {string} id 项目 hashid
 * @returns {Promise<Object>} 与 getProjectPromotion 相同的状态结构
 */
export const cancelProjectPromotion = (token, id) =>
  authRequest(token, {
    url: `/projects/${id}/promotion`,
    method: "DELETE",
  }).then((res) => res.data);

/**
 * @param {string} userId
 * @param {string} [token]
 * @returns {Promise<{projects: Array, total_count: number}>}
 */
export const getUserProjects = (userId, token) => {
  const req = token
    ? authRequest(token, { url: `/users/${userId}/projects` })
    : request({ url: `/users/${userId}/projects` });
  return req.then((res) => res.data);
};
